import { Anchor, Group, Stack, Text } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useClient } from 'src/clients/hooks/useClient';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { FoundPersonSummary, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { Button } from 'src/design-system/Button';
import { DateInputWithLabel } from 'src/design-system/DateInputWithLabel';
import { InputWithLabel } from 'src/design-system/InputWithLabel';
import { Modal } from 'src/design-system/Modal';
import { Select } from 'src/design-system/Select';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatBirthDate, roleNameFromRelationship, splitName } from './foundPersonDetails';
import { useAddFoundPersonToFamily, useFoundPerson } from './useFoundPerson';

type Props = {
  file: UploadedFile;
  person: FoundPersonSummary;
  relativeFirstName: string | null;
  opened: boolean;
  onClose: () => void;
};

const formSchema = z.object({
  firstName: z.string().trim().min(1, 'Please enter a first name'),
  lastName: z.string().trim().min(1, 'Please enter a last name'),
  dateOfBirth: z.string().nullable(),
  familyRoleId: z.string().min(1, 'Please choose a family role'),
  familyName: z.string(),
});

export function AddFoundPersonModal({ file, person, relativeFirstName, opened, onClose }: Props) {
  const { first, last } = splitName(person.name);
  const relative = relativeFirstName ?? 'the client';
  const { client } = useClient(opened ? file.clientId : undefined);
  const existingFamily = client?.family ?? null;
  const hasFamily = !!(existingFamily || client?.familyId);
  const { familyRoles, isLoading: rolesLoading } = useFamilyRoles({ enabled: opened });
  const { data: found } = useFoundPerson(file, person.id, { enabled: opened });
  const add = useAddFoundPersonToFamily(file, person);
  const [useExistingClient, setUseExistingClient] = useState(false);
  const matchingClient = found?.matchingClient ?? null;

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: first,
      lastName: last,
      dateOfBirth: person.dateOfBirth ?? null,
      familyRoleId: '',
      familyName: last ? `${last} family` : '',
    },
    validate: schemaResolver(formSchema, { sync: true }),
    validateInputOnBlur: true,
  });

  const suggestedRole = roleNameFromRelationship(person.relationship);
  useEffect(() => {
    const role = familyRoles.find(({ name }) => name === suggestedRole);
    if (opened && role && !form.values.familyRoleId) form.setFieldValue('familyRoleId', role.id);
  }, [opened, familyRoles, suggestedRole]);

  const needsFamilyName = !hasFamily && !form.values.familyName.trim();
  const canSubmit = !!form.values.familyRoleId && !needsFamilyName && !rolesLoading;

  const handleSubmit = (values: typeof form.values) =>
    add.mutate(
      {
        body: {
          ...(useExistingClient && matchingClient
            ? { existingClientId: matchingClient.id }
            : {
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                dateOfBirth: values.dateOfBirth,
              }),
          familyRoleId: values.familyRoleId,
          ...(hasFamily ? {} : { familyName: values.familyName.trim() }),
        },
        joining: existingFamily?.name ?? values.familyName.trim(),
      },
      { onSuccess: onClose },
    );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={540}
      title={`Add ${person.name} to ${relative}'s family`}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm" data-testid="add-found-person-modal">
          <Text size="xs" c="gray.7">
            Filled in from {file.file.fileName}. Check the details, then add {first}.
          </Text>
          {matchingClient && (
            <Group
              gap="xs"
              justify="space-between"
              bg="gray.0"
              p="xs"
              style={{ borderRadius: 8 }}
              data-testid="matching-client-line"
            >
              <Text size="sm" style={{ flex: 1, minWidth: 200 }}>
                {useExistingClient
                  ? `We'll use the existing client, ${person.name}.`
                  : `${person.name}, born ${formatBirthDate(person.dateOfBirth)}, is already a client.`}
              </Text>
              <Anchor
                component="button"
                type="button"
                size="sm"
                fw={FONT_WEIGHTS.medium}
                c="brand.7"
                onClick={() => setUseExistingClient(previous => !previous)}
              >
                {useExistingClient ? 'Create a new client instead' : 'Use this client instead'}
              </Anchor>
            </Group>
          )}
          {!useExistingClient && (
            <>
              <Group gap="xs" align="flex-start" grow>
                <InputWithLabel label="First name" {...form.getInputProps('firstName')} />
                <InputWithLabel label="Last name" {...form.getInputProps('lastName')} />
              </Group>
              <DateInputWithLabel
                label="Date of birth"
                valueFormat="DD/MM/YYYY"
                clearable
                {...form.getInputProps('dateOfBirth')}
              />
            </>
          )}
          <Select
            label="Family role"
            placeholder="Please select"
            data={familyRoles.map(role => ({ value: role.id, label: role.name }))}
            {...form.getInputProps('familyRoleId')}
          />
          {!hasFamily && (
            <InputWithLabel
              label="Family name"
              description={`${relative} isn't in a family yet, so this creates one.`}
              {...form.getInputProps('familyName')}
            />
          )}
          {existingFamily && (
            <Text size="sm">
              {first} joins {existingFamily.name}.
            </Text>
          )}
          <Stack gap={4} bg="gray.0" p="xs" style={{ borderRadius: 8 }}>
            <Text size="xs" fw={FONT_WEIGHTS.semibold}>
              What happens next
            </Text>
            <Text size="xs">
              {first} becomes a client of {relative}&apos;s adviser, because a family has one
              adviser.
            </Text>
            <Text size="xs">
              What we kept moves to {first}&apos;s own review, ready straight away. Nothing is read
              again.
            </Text>
            {person.sharedCount > 0 && (
              <Text size="xs">
                Items shared with {relative}, other than cash flows, become joint between {relative}{' '}
                and {first}.
              </Text>
            )}
          </Stack>
          {add.error && (
            <Text size="sm" c="error.7">
              {add.error.message}
            </Text>
          )}
          <Group justify="flex-end" gap="sm" mt="xs">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={!canSubmit}
              loading={add.isPending}
            >
              Add {first}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
